import { useGetAllOrganizationQuery } from '@codezo/graphql';
import { Check, Plus } from 'lucide-react';
import { useState } from 'react';
import { Popover, PopoverContent } from '../ui/popover';
import {
	SelectSearchInput,
	SelectTrigger,
	type SelectTriggerVariant,
} from '../ui/select-primitives';
import { cn } from '../../lib/utils';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface OrganizationSelectorProps {
	value: string;
	/** `label` is the picked client's name — keep it if the selector may unmount (e.g. dialog steps). */
	onChange: (v: string, label?: string) => void;
	placeholder?: string;
	skip?: boolean;
	selectedLabel?: string;
	/** Allow clearing the selection */
	clearable?: boolean;
	/** Visual treatment: `form` (default) or `toolbar` for list-page filters */
	variant?: SelectTriggerVariant;
	/** Extra classes merged into the trigger (e.g. width on a toolbar). */
	className?: string;
	/** Non-interactive, fixed selection (e.g. locked to a scoped organization). */
	disabled?: boolean;
	/**
	 * When set, renders a "Create new client" action pinned below the list.
	 * Called with the current search text so the create form can be prefilled.
	 */
	onCreateNew?: (query: string) => void;
}

export function OrganizationSelector({
	value,
	onChange,
	placeholder = 'Select client',
	skip = false,
	selectedLabel: selectedLabelProp,
	clearable = false,
	variant = 'form',
	className,
	disabled = false,
	onCreateNew,
}: OrganizationSelectorProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	/** Remembers what was picked here, so a stale label is never shown for another value. */
	const [picked, setPicked] = useState<{ id: string; label: string } | null>(null);
	const debouncedQuery = useDebouncedValue(query);
	const [page, setPage] = useState(1);

	const { data, loading } = useGetAllOrganizationQuery({
		variables: {
			input: {
				pagination: { limit: 20, currentPage: page },
				search: debouncedQuery || undefined,
			},
		},
		fetchPolicy: 'no-cache',
		skip: skip || !open,
	});

	const scroll = useInfiniteScroll({
		items: data?.getAllOrganization?.data,
		pageInfo: data?.getAllOrganization?.pageInfo,
		loading,
		resetKey: debouncedQuery,
	});

	if (scroll.page !== page) setPage(scroll.page);

	const displayLabel =
		(picked?.id === value ? picked.label : undefined) || selectedLabelProp || undefined;

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange('');
		setPicked(null);
	};

	return (
		<Popover
			open={disabled ? false : open}
			onOpenChange={(next) => {
				if (disabled) return;
				setOpen(next);
				if (!next) {
					setQuery('');
					scroll.reset();
					setPage(1);
				}
			}}
		>
			<SelectTrigger
				label={displayLabel}
				placeholder={placeholder}
				hasValue={!!value}
				variant={variant}
				className={className}
				disabled={disabled}
				onClear={clearable && !disabled ? handleClear : undefined}
			/>
			<PopoverContent align="start" className="w-(--anchor-width) gap-0 p-0">
				<SelectSearchInput value={query} onChange={setQuery} />
				<div className="scroll-thin max-h-[200px] overflow-y-auto p-1">
					{scroll.initialLoading && (
						<div className="py-4 text-center text-xs text-text-4">Loading…</div>
					)}
					{!loading && scroll.items.length === 0 && (
						<div className="px-2 py-3 text-center text-sm text-text-4">
							{query ? 'No results' : 'No clients'}
						</div>
					)}
					{scroll.items.map((org) => (
						<button
							key={org.id}
							onClick={() => {
								if (clearable && value === org.id) {
									onChange('');
									setPicked(null);
								} else {
									onChange(org.id, org.name);
									setPicked({ id: org.id, label: org.name });
								}
								setOpen(false);
								setQuery('');
							}}
							className={cn(
								'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
								value === org.id
									? 'bg-surface-fill text-text-1'
									: 'text-text-2 hover:bg-surface-hover hover:text-text-1'
							)}
						>
							<Check
								size={12}
								className={cn(
									'shrink-0',
									value === org.id ? 'text-accent' : 'invisible'
								)}
							/>
							{org.name}
						</button>
					))}
					{scroll.loadingMore && (
						<div className="py-1 text-center text-2xs text-text-4">Loading more…</div>
					)}
					<div ref={scroll.sentinelRef} className="h-1" />
				</div>
				{onCreateNew && (
					<div className="border-t border-line-1 p-1">
						<button
							type="button"
							onClick={() => {
								const seed = query.trim();
								setOpen(false);
								setQuery('');
								onCreateNew(seed);
							}}
							className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
						>
							<Plus size={12} className="shrink-0 text-text-4" />
							Create new client
						</button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
